import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../../../api/client';
import {
  PRIMARY,
  TEXT_DARK,
  TEXT_MUTED,
  BORDER_LIGHT,
  BG_SURFACE,
  BG_CARD,
} from '../designTokens';

// ─── Types ────────────────────────────────────────────────────────────────────

interface FamilyGroupMember {
  id: string;
  fullName: string;
  ticketNumber: string | null;
  chapterAffiliation: string | null;
  orderId: string | null;
  tierName?: string | null;
  seatLabel?: string | null;
  tableName?: string | null;
  zoneName?: string | null;
}

interface FamilyGroup {
  id: string;
  name: string;
  active: boolean;
  locked: boolean;
  memberCount: number;
  members: FamilyGroupMember[];
  placement?: {
    zone: string;
    tables: { tableNumber: number; tableName: string; zoneName: string }[];
  };
  createdAt: string;
  updatedAt: string;
}

interface GuestSearchResult {
  id: string;
  fullName: string;
  ticketNumber: string | null;
  familyAssociation?: string | null;
}

interface FamilySuggestionMember {
  id: string;
  fullName: string;
  ticketNumber: string | null;
  orderId: string | null;
  chapter: string | null;
}

interface FamilySuggestion {
  surname: string;
  confidence: 'High' | 'Medium' | 'Low';
  members: FamilySuggestionMember[];
}

interface SeatingZone {
  id: string;
  name: string;
  colorCode?: string;
}

interface BatchSeatResult {
  success: boolean;
  seated: number;
  tablesUsed: string[];
  zonePlacements: { zoneId: string; zoneName: string; count: number }[];
  groupResults: {
    groupId: string;
    groupName: string;
    membersSeated: number;
    tables: { tableNumber: number; tableName: string; zoneName: string; seatCount: number }[];
  }[];
  unplaced?: { guestId: string; fullName: string; reason: string }[];
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  container: {},
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
  },
  createRow: {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'center',
  },
  input: {
    padding: '0.4rem 0.6rem',
    fontSize: '0.82rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '4px',
    outline: 'none',
    width: '180px',
  },
  btnPrimary: {
    padding: '0.4rem 0.8rem',
    fontSize: '0.8rem',
    fontWeight: 600,
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  btnSecondary: {
    padding: '0.35rem 0.7rem',
    fontSize: '0.78rem',
    fontWeight: 500,
    backgroundColor: '#e0e0e0',
    color: '#333',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  btnDanger: {
    padding: '0.35rem 0.7rem',
    fontSize: '0.78rem',
    fontWeight: 500,
    backgroundColor: '#d32f2f',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  detectBtn: {
    padding: '0.4rem 0.8rem',
    fontSize: '0.8rem',
    fontWeight: 600,
    backgroundColor: '#1565c0',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    marginRight: '0.5rem',
  },
  card: {
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '6px',
    marginBottom: '0.75rem',
    backgroundColor: BG_CARD,
    overflow: 'hidden',
  },
  cardInactive: {
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '6px',
    marginBottom: '0.75rem',
    backgroundColor: '#fafafa',
    overflow: 'hidden',
    opacity: 0.7,
  },
  cardLocked: {
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '6px',
    marginBottom: '0.75rem',
    backgroundColor: '#f9fafb',
    overflow: 'hidden',
    opacity: 0.8,
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.65rem 0.85rem',
    cursor: 'pointer',
  },
  cardLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.6rem',
  },
  groupName: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: TEXT_DARK,
  },
  badge: {
    fontSize: '0.7rem',
    padding: '0.15rem 0.4rem',
    borderRadius: '10px',
    backgroundColor: BG_SURFACE,
    color: TEXT_MUTED,
    fontWeight: 500,
  },
  inactiveBadge: {
    fontSize: '0.68rem',
    padding: '0.12rem 0.35rem',
    borderRadius: '3px',
    backgroundColor: '#ffebee',
    color: '#c62828',
    fontWeight: 500,
    marginLeft: '0.4rem',
  },
  seatedBadge: {
    fontSize: '0.68rem',
    padding: '0.12rem 0.35rem',
    borderRadius: '3px',
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    fontWeight: 600,
    marginLeft: '0.4rem',
  },
  cardActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.3rem',
  },
  iconBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px 6px',
    fontSize: '0.9rem',
    borderRadius: '4px',
    lineHeight: 1,
    transition: 'background-color 0.15s',
  },
  toggleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.35rem',
  },
  toggleLabel: {
    fontSize: '0.7rem',
    color: TEXT_MUTED,
  },
  cardBody: {
    padding: '0.5rem 0.85rem 0.75rem',
    borderTop: `1px solid ${BORDER_LIGHT}`,
  },
  memberRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.3rem 0',
    fontSize: '0.8rem',
    color: TEXT_DARK,
  },
  memberInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
  },
  ticketLabel: {
    fontSize: '0.72rem',
    color: TEXT_MUTED,
  },
  addMemberRow: {
    display: 'flex',
    gap: '0.4rem',
    alignItems: 'center',
    marginTop: '0.5rem',
    paddingTop: '0.5rem',
    borderTop: `1px dashed ${BORDER_LIGHT}`,
  },
  searchInput: {
    padding: '0.35rem 0.5rem',
    fontSize: '0.78rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '4px',
    outline: 'none',
    width: '160px',
  },
  searchResults: {
    position: 'absolute' as const,
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: BG_CARD,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '4px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    maxHeight: '150px',
    overflowY: 'auto' as const,
    zIndex: 10,
  },
  searchResultItem: {
    padding: '0.4rem 0.6rem',
    fontSize: '0.78rem',
    cursor: 'pointer',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
    color: TEXT_DARK,
  },
  emptyState: {
    textAlign: 'center' as const,
    padding: '2rem 1rem',
    color: TEXT_MUTED,
    fontSize: '0.85rem',
  },
  error: {
    padding: '0.5rem 0.75rem',
    color: '#d32f2f',
    fontSize: '0.8rem',
    backgroundColor: '#fdecea',
    borderRadius: '4px',
    marginBottom: '0.75rem',
  },
  editInput: {
    padding: '0.3rem 0.5rem',
    fontSize: '0.82rem',
    border: `1px solid ${PRIMARY}`,
    borderRadius: '4px',
    outline: 'none',
    width: '150px',
  },
  // Suggestion styles
  suggestionsSection: {
    marginBottom: '1.5rem',
  },
  suggestionsSectionTitle: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: TEXT_DARK,
    marginBottom: '0.5rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
  },
  suggestionCard: {
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '6px',
    marginBottom: '0.6rem',
    backgroundColor: BG_CARD,
    padding: '0.7rem 0.85rem',
  },
  suggestionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '0.4rem',
  },
  suggestionSurname: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: TEXT_DARK,
    textTransform: 'capitalize' as const,
  },
  confidenceBadge: {
    fontSize: '0.68rem',
    padding: '0.12rem 0.4rem',
    borderRadius: '10px',
    fontWeight: 600,
  },
  suggestionMember: {
    fontSize: '0.78rem',
    color: TEXT_DARK,
    padding: '0.2rem 0',
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
  },
  suggestionActions: {
    display: 'flex',
    gap: '0.4rem',
    marginTop: '0.5rem',
    paddingTop: '0.4rem',
    borderTop: `1px dashed ${BORDER_LIGHT}`,
  },
  approveBtn: {
    padding: '0.3rem 0.6rem',
    fontSize: '0.75rem',
    fontWeight: 600,
    backgroundColor: '#2e7d32',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  dismissBtn: {
    padding: '0.3rem 0.6rem',
    fontSize: '0.75rem',
    fontWeight: 500,
    backgroundColor: '#e0e0e0',
    color: '#333',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  // Selection toolbar styles (Task 7.2)
  selectionToolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.6rem 0.85rem',
    backgroundColor: '#e3f2fd',
    border: '1px solid #90caf9',
    borderRadius: '6px',
    marginBottom: '0.75rem',
  },
  selectionInfo: {
    fontSize: '0.82rem',
    fontWeight: 500,
    color: '#1565c0',
  },
  // Config panel styles (Task 8.1)
  configOverlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  configPanel: {
    backgroundColor: BG_CARD,
    borderRadius: '8px',
    padding: '1.5rem',
    width: '420px',
    maxWidth: '90vw',
    boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
  },
  configTitle: {
    fontSize: '1rem',
    fontWeight: 700,
    color: TEXT_DARK,
    marginBottom: '1rem',
  },
  configField: {
    marginBottom: '1rem',
  },
  configLabel: {
    display: 'block',
    fontSize: '0.78rem',
    fontWeight: 600,
    color: TEXT_DARK,
    marginBottom: '0.35rem',
  },
  configSelect: {
    width: '100%',
    padding: '0.4rem 0.6rem',
    fontSize: '0.82rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '4px',
    outline: 'none',
  },
  configRadioGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.4rem',
  },
  configRadioLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    fontSize: '0.8rem',
    color: TEXT_DARK,
    cursor: 'pointer',
  },
  configActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.5rem',
    marginTop: '1.25rem',
  },
  // Section header styles (Task 9.1)
  sectionTitle: {
    fontSize: '0.82rem',
    fontWeight: 600,
    color: TEXT_MUTED,
    marginBottom: '0.5rem',
    marginTop: '1rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.03em',
  },
  placementInfo: {
    fontSize: '0.72rem',
    color: TEXT_MUTED,
    marginTop: '0.2rem',
  },
  unseatBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px 6px',
    fontSize: '0.72rem',
    color: '#d32f2f',
    fontWeight: 500,
    borderRadius: '4px',
    lineHeight: 1,
    transition: 'background-color 0.15s',
  },
  // Success/warning messages
  successMsg: {
    padding: '0.5rem 0.75rem',
    color: '#2e7d32',
    fontSize: '0.8rem',
    backgroundColor: '#e8f5e9',
    borderRadius: '4px',
    marginBottom: '0.75rem',
  },
  warningMsg: {
    padding: '0.5rem 0.75rem',
    color: '#e65100',
    fontSize: '0.8rem',
    backgroundColor: '#fff3e0',
    borderRadius: '4px',
    marginBottom: '0.75rem',
  },
};

const confidenceColors: Record<string, { bg: string; color: string }> = {
  High: { bg: '#e8f5e9', color: '#2e7d32' },
  Medium: { bg: '#fff8e1', color: '#f9a825' },
  Low: { bg: '#eceff1', color: '#546e7a' },
};


// ─── Component ────────────────────────────────────────────────────────────────

export function FamilyGroupsSection() {
  const [groups, setGroups] = useState<FamilyGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Create group state
  const [showCreate, setShowCreate] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [creating, setCreating] = useState(false);

  // Edit name state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  // Add member modal state
  const [addMemberModalGroupId, setAddMemberModalGroupId] = useState<string | null>(null);
  const [pendingMembers, setPendingMembers] = useState<GuestSearchResult[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<GuestSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [savingMembers, setSavingMembers] = useState(false);

  // Delete confirm state
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  // Family detection state
  const [suggestions, setSuggestions] = useState<FamilySuggestion[]>([]);
  const [detecting, setDetecting] = useState(false);

  // ─── Task 7.1: Selection state ────────────────────────────────────────────────
  const [selectedGroupIds, setSelectedGroupIds] = useState<Set<string>>(new Set());

  // ─── View mode toggle: 'cards' (expandable cards) or 'table' (3-pane structured) ───
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('table');
  const [tableViewSelectedId, setTableViewSelectedId] = useState<string | null>(null);

  // ─── Task 8.1: Config panel state ─────────────────────────────────────────────
  const [showConfigPanel, setShowConfigPanel] = useState(false);
  const [configZoneId, setConfigZoneId] = useState<string | null>(null);
  const [configRule, setConfigRule] = useState<'keep_together' | 'allow_split' | 'start_new_table'>('keep_together');
  const [zones, setZones] = useState<SeatingZone[]>([]);
  const [seatingInProgress, setSeatingInProgress] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [warningMessage, setWarningMessage] = useState('');

  const fetchGroups = useCallback(async () => {
    try {
      const data = await apiClient<FamilyGroup[]>('/batch-assignment/family-groups');
      setGroups(data);
      setError('');
    } catch {
      setError('Failed to load family groups.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  // Fetch zones for config panel
  useEffect(() => {
    apiClient<SeatingZone[]>('/table-management/zones')
      .then((data) => setZones(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  // Search guests with debounce (for add member modal)
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2 || !addMemberModalGroupId) {
      setSearchResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const data = await apiClient<any>(
          `/table-management/guests?query=${encodeURIComponent(searchQuery)}&limit=10`,
        );
        const guests = Array.isArray(data) ? data : (data.data || data.guests || []);
        const group = groups.find((g) => g.id === addMemberModalGroupId);
        const memberIds = new Set(group?.members.map((m) => m.id) ?? []);
        const pendingIds = new Set(pendingMembers.map((m) => m.id));
        const filtered = guests.filter(
          (g: GuestSearchResult) => !memberIds.has(g.id) && !pendingIds.has(g.id),
        );
        setSearchResults(Array.isArray(filtered) ? filtered : []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, addMemberModalGroupId, groups, pendingMembers]);

  // ─── Task 7.1: Selection handlers ─────────────────────────────────────────────

  const selectableGroups = groups.filter((g) => g.active && !g.locked);
  const lockedGroups = groups.filter((g) => g.locked);
  const inactiveGroups = groups.filter((g) => !g.active && !g.locked);

  const handleToggleSelect = (groupId: string) => {
    setSelectedGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  const selectedMemberCount = selectableGroups
    .filter((g) => selectedGroupIds.has(g.id))
    .reduce((sum, g) => sum + g.memberCount, 0);

  // ─── Task 8.1: Seating config handlers ────────────────────────────────────────

  const handleOpenConfig = () => {
    setShowConfigPanel(true);
    setConfigZoneId(null);
    setConfigRule('keep_together');
  };

  const handleConfirmSeat = async () => {
    setSeatingInProgress(true);
    setError('');
    setSuccessMessage('');
    setWarningMessage('');
    try {
      const result = await apiClient<BatchSeatResult>('/batch-assignment/family-groups/seat', {
        method: 'POST',
        body: {
          groupIds: Array.from(selectedGroupIds),
          zoneId: configZoneId,
          rule: configRule,
        },
      });

      if (result.success) {
        setSuccessMessage(`Successfully seated ${result.seated} members across ${result.tablesUsed.length} table${result.tablesUsed.length !== 1 ? 's' : ''}.`);
      } else if (result.unplaced && result.unplaced.length > 0) {
        const unplacedNames = result.unplaced.map((u) => u.fullName).join(', ');
        setWarningMessage(`Partial result: ${result.seated} seated, but could not place: ${unplacedNames}`);
      }

      setShowConfigPanel(false);
      setSelectedGroupIds(new Set());
      await fetchGroups();
    } catch (err: any) {
      // Keep popup open so user can change selections
      setError(err?.message || 'Failed to seat family groups.');
    } finally {
      setSeatingInProgress(false);
    }
  };

  // ─── Task 9.2: Unseat handler ─────────────────────────────────────────────────

  const handleUnseat = async (group: FamilyGroup) => {
    const confirmed = window.confirm(
      `Are you sure you want to unseat all members of ${group.name}?`,
    );
    if (!confirmed) return;

    setError('');
    setSuccessMessage('');
    try {
      await apiClient(`/batch-assignment/family-groups/${group.id}/unseat`, {
        method: 'POST',
      });
      setSuccessMessage(`Unseated all members of ${group.name}.`);
      await fetchGroups();
    } catch (err: any) {
      setError(err?.message || 'Failed to unseat family group.');
    }
  };

  // ─── Existing handlers ─────────────────────────────────────────────────────────

  const handleCreate = async () => {
    if (!newGroupName.trim()) return;
    setCreating(true);
    setError('');
    try {
      await apiClient('/batch-assignment/family-groups', {
        method: 'POST',
        body: { name: newGroupName.trim() },
      });
      setNewGroupName('');
      setShowCreate(false);
      await fetchGroups();
    } catch (err: any) {
      setError(err?.message || 'Failed to create family group.');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleActive = async (group: FamilyGroup) => {
    try {
      await apiClient(`/batch-assignment/family-groups/${group.id}`, {
        method: 'PATCH',
        body: { active: !group.active },
      });
      await fetchGroups();
    } catch (err: any) {
      setError(err?.message || 'Failed to update group.');
    }
  };

  const handleSaveEdit = async (groupId: string) => {
    if (!editName.trim()) return;
    try {
      await apiClient(`/batch-assignment/family-groups/${groupId}`, {
        method: 'PATCH',
        body: { name: editName.trim() },
      });
      setEditingId(null);
      await fetchGroups();
    } catch (err: any) {
      setError(err?.message || 'Failed to rename group.');
    }
  };

  const handleDelete = async (groupId: string) => {
    try {
      await apiClient(`/batch-assignment/family-groups/${groupId}`, {
        method: 'DELETE',
      });
      setConfirmDeleteId(null);
      await fetchGroups();
    } catch (err: any) {
      setError(err?.message || 'Failed to delete group.');
    }
  };

  const handleAddMember = async (groupId: string, guestId: string) => {
    try {
      await apiClient(`/batch-assignment/family-groups/${groupId}/members`, {
        method: 'POST',
        body: { guestId },
      });
      setSearchQuery('');
      setSearchResults([]);
      await fetchGroups();
    } catch (err: any) {
      setError(err?.message || 'Failed to add member.');
    }
  };

  // ─── Add Members Modal Handlers ─────────────────────────────────────────────

  const handleTogglePendingMember = (guest: GuestSearchResult) => {
    setPendingMembers((prev) => {
      const exists = prev.find((m) => m.id === guest.id);
      if (exists) {
        return prev.filter((m) => m.id !== guest.id);
      }
      return [...prev, guest];
    });
  };

  const handleRemovePendingMember = (guestId: string) => {
    setPendingMembers((prev) => prev.filter((m) => m.id !== guestId));
  };

  const handleSaveMembers = async () => {
    if (!addMemberModalGroupId || pendingMembers.length === 0) return;
    setSavingMembers(true);
    setError('');
    try {
      for (const guest of pendingMembers) {
        await handleAddMember(addMemberModalGroupId, guest.id);
      }
      setPendingMembers([]);
      setSearchQuery('');
      setSearchResults([]);
      setAddMemberModalGroupId(null);
      await fetchGroups();
    } catch (err: any) {
      setError(err?.message || 'Failed to add members.');
    } finally {
      setSavingMembers(false);
    }
  };

  const handleCloseAddMemberModal = () => {
    setAddMemberModalGroupId(null);
    setPendingMembers([]);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleRemoveMember = async (groupId: string, guestId: string) => {
    try {
      await apiClient(`/batch-assignment/family-groups/${groupId}/members/${guestId}`, {
        method: 'DELETE',
      });
      await fetchGroups();
    } catch (err: any) {
      setError(err?.message || 'Failed to remove member.');
    }
  };

  // ─── Family Detection Handlers ──────────────────────────────────────────────

  const handleDetectFamilies = async () => {
    setDetecting(true);
    setError('');
    try {
      const data = await apiClient<FamilySuggestion[]>(
        '/batch-assignment/family-groups/detect',
        { method: 'POST' },
      );
      setSuggestions(data);
    } catch (err: any) {
      setError(err?.message || 'Failed to detect families.');
    } finally {
      setDetecting(false);
    }
  };

  const handleClearDismissed = async () => {
    if (!window.confirm('Clear all dismissed suggestions? This will allow previously dismissed families to reappear on the next detection run.')) return;
    setError('');
    try {
      await apiClient('/batch-assignment/family-groups/dismissed', { method: 'DELETE' });
      setSuggestions([]);
    } catch (err: any) {
      setError(err?.message || 'Failed to clear dismissed suggestions.');
    }
  };

  const handleApproveSuggestion = async (suggestion: FamilySuggestion) => {
    try {
      const name =
        suggestion.surname.charAt(0).toUpperCase() + suggestion.surname.slice(1) + ' Family';
      await apiClient('/batch-assignment/family-groups/approve-suggestion', {
        method: 'POST',
        body: { name, guestIds: suggestion.members.map((m) => m.id) },
      });
      setSuggestions((prev) => prev.filter((s) => s.surname !== suggestion.surname));
      await fetchGroups();
    } catch (err: any) {
      setError(err?.message || 'Failed to approve suggestion.');
    }
  };

  const handleDismissSuggestion = async (suggestion: FamilySuggestion) => {
    try {
      await apiClient('/batch-assignment/family-groups/dismiss', {
        method: 'POST',
        body: {
          surname: suggestion.surname,
          guestIds: suggestion.members.map((m) => m.id),
        },
      });
      setSuggestions((prev) => prev.filter((s) => s.surname !== suggestion.surname));
    } catch (err: any) {
      setError(err?.message || 'Failed to dismiss suggestion.');
    }
  };

  if (loading) {
    return <div style={{ padding: '1rem', color: TEXT_MUTED, fontSize: '0.85rem' }}>Loading family groups...</div>;
  }


  return (
    <div style={styles.container}>
      {error && <div style={styles.error} role="alert">{error}</div>}
      {successMessage && <div style={styles.successMsg}>{successMessage}</div>}
      {warningMessage && <div style={styles.warningMsg} role="alert">{warningMessage}</div>}

      <div style={styles.toolbar}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span style={{ fontSize: '0.82rem', color: TEXT_MUTED }}>
            {groups.length} group{groups.length !== 1 ? 's' : ''}
          </span>
          {/* View mode toggle */}
          <div style={{ display: 'flex', border: `1px solid ${BORDER_LIGHT}`, borderRadius: '4px', overflow: 'hidden' }}>
            <button
              style={{
                padding: '0.3rem 0.6rem',
                fontSize: '0.75rem',
                border: 'none',
                cursor: 'pointer',
                backgroundColor: viewMode === 'table' ? PRIMARY : '#f5f5f5',
                color: viewMode === 'table' ? '#fff' : TEXT_MUTED,
                fontWeight: 600,
              }}
              onClick={() => setViewMode('table')}
              aria-label="Table view"
              title="Table view"
            >
              📊 Table
            </button>
            <button
              style={{
                padding: '0.3rem 0.6rem',
                fontSize: '0.75rem',
                border: 'none',
                borderLeft: `1px solid ${BORDER_LIGHT}`,
                cursor: 'pointer',
                backgroundColor: viewMode === 'cards' ? PRIMARY : '#f5f5f5',
                color: viewMode === 'cards' ? '#fff' : TEXT_MUTED,
                fontWeight: 600,
              }}
              onClick={() => setViewMode('cards')}
              aria-label="Card view"
              title="Card view"
            >
              🃏 Cards
            </button>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <button
            style={styles.detectBtn}
            onClick={handleDetectFamilies}
            disabled={detecting}
            aria-label="Detect potential family groups"
          >
            {detecting ? 'Detecting...' : '🔍 Detect Families'}
          </button>
          <button
            style={{ ...styles.btnSecondary, marginRight: '0.5rem', fontSize: '0.75rem' }}
            onClick={handleClearDismissed}
            aria-label="Reset dismissed suggestions"
            title="Clear all dismissed suggestions so they can be re-detected"
          >
            🔄 Reset Dismissed
          </button>
          {!showCreate ? (
            <button style={styles.btnPrimary} onClick={() => setShowCreate(true)}>
              + Create Group
            </button>
          ) : (
            <div style={styles.createRow}>
              <input
                style={styles.input}
                type="text"
                placeholder="Family group name"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                autoFocus
                aria-label="New family group name"
              />
              <button
                style={styles.btnPrimary}
                onClick={handleCreate}
                disabled={creating || !newGroupName.trim()}
              >
                {creating ? '...' : 'Save'}
              </button>
              <button
                style={styles.btnSecondary}
                onClick={() => { setShowCreate(false); setNewGroupName(''); }}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Family Suggestions */}
      {suggestions.length > 0 && (
        <div style={styles.suggestionsSection}>
          <div style={styles.suggestionsSectionTitle}>
            <span>💡 Detected Family Suggestions ({suggestions.length})</span>
          </div>
          {suggestions.map((suggestion) => {
            const colors = confidenceColors[suggestion.confidence];
            return (
              <div key={suggestion.surname} style={styles.suggestionCard}>
                <div style={styles.suggestionHeader}>
                  <span style={styles.suggestionSurname}>{suggestion.surname} Family</span>
                  <span
                    style={{
                      ...styles.confidenceBadge,
                      backgroundColor: colors.bg,
                      color: colors.color,
                    }}
                  >
                    {suggestion.confidence}
                  </span>
                </div>
                {suggestion.members.map((member) => (
                  <div key={member.id} style={styles.suggestionMember}>
                    <span>{member.fullName}</span>
                    {member.ticketNumber && (
                      <span style={{ fontSize: '0.7rem', color: TEXT_MUTED }}>
                        {member.ticketNumber}
                      </span>
                    )}
                    {member.chapter && (
                      <span style={{ fontSize: '0.68rem', color: '#1565c0', backgroundColor: '#e3f2fd', padding: '0.08rem 0.3rem', borderRadius: '3px' }}>
                        {member.chapter}
                      </span>
                    )}
                  </div>
                ))}
                <div style={styles.suggestionActions}>
                  <button
                    style={styles.approveBtn}
                    onClick={() => handleApproveSuggestion(suggestion)}
                    aria-label={`Approve ${suggestion.surname} family suggestion`}
                  >
                    ✓ Approve
                  </button>
                  <button
                    style={styles.dismissBtn}
                    onClick={() => handleDismissSuggestion(suggestion)}
                    aria-label={`Dismiss ${suggestion.surname} family suggestion`}
                  >
                    ✕ Dismiss
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {groups.length === 0 && suggestions.length === 0 && (
        <div style={styles.emptyState}>
          No family groups created yet. Create one to start managing family associations,
          or use "Detect Families" to find suggestions.
        </div>
      )}

      {/* ─── Task 7.2: Selection Toolbar ─────────────────────────────────────── */}
      {selectedGroupIds.size > 0 && viewMode === 'cards' && (
        <div style={styles.selectionToolbar}>
          <span style={styles.selectionInfo}>
            {selectedGroupIds.size} group{selectedGroupIds.size !== 1 ? 's' : ''} selected ({selectedMemberCount} member{selectedMemberCount !== 1 ? 's' : ''})
          </span>
          <button
            style={styles.btnPrimary}
            onClick={handleOpenConfig}
            disabled={selectedGroupIds.size === 0}
            aria-label="Seat selected family groups"
          >
            🪑 Seat Selected
          </button>
        </div>
      )}

      {/* ─── Active Groups (selectable) ──────────────────────────────────────── */}
      {viewMode === 'cards' && selectableGroups.length > 0 && (
        <>
          {(lockedGroups.length > 0 || inactiveGroups.length > 0) && (
            <div style={styles.sectionTitle}>Active Groups</div>
          )}
          {selectableGroups.map((group) => (
            <div key={group.id} style={styles.card}>
              <div
                style={styles.cardHeader}
                onClick={() => setExpandedId(expandedId === group.id ? null : group.id)}
                role="button"
                aria-expanded={expandedId === group.id}
                aria-label={`Toggle ${group.name} details`}
              >
                <div style={styles.cardLeft}>
                  {/* Task 7.1: Checkbox for selection */}
                  <input
                    type="checkbox"
                    checked={selectedGroupIds.has(group.id)}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleToggleSelect(group.id);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    aria-label={`Select ${group.name} for batch seating`}
                  />
                  <span style={{ fontSize: '0.75rem', color: TEXT_MUTED }}>
                    {expandedId === group.id ? '▼' : '▶'}
                  </span>
                  {editingId === group.id ? (
                    <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
                      <input
                        style={styles.editInput}
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveEdit(group.id);
                          if (e.key === 'Escape') setEditingId(null);
                        }}
                        autoFocus
                        aria-label="Edit group name"
                      />
                      <button style={styles.iconBtn} onClick={() => handleSaveEdit(group.id)} title="Save" aria-label="Save name">✓</button>
                      <button style={styles.iconBtn} onClick={() => setEditingId(null)} title="Cancel" aria-label="Cancel edit">✕</button>
                    </div>
                  ) : (
                    <span style={styles.groupName}>{group.name}</span>
                  )}
                  <span style={styles.badge}>{group.memberCount} member{group.memberCount !== 1 ? 's' : ''}</span>
                </div>

                <div style={styles.cardActions} onClick={(e) => e.stopPropagation()}>
                  {group.placement ? (
                    <span style={styles.seatedBadge}>✅ Seated</span>
                  ) : (
                    <button
                      style={{ ...styles.btnPrimary, padding: '0.3rem 0.6rem', fontSize: '0.72rem' }}
                      onClick={async () => {
                        try {
                          const result = await apiClient<{ tableNumber: number; seatedGuests: { fullName: string }[]; skippedGuests: { fullName: string; reason: string }[] }>('/table-management/seat-family', {
                            method: 'POST',
                            body: { familyAssociation: group.name },
                          });
                          setError('');
                          fetchGroups();
                          alert(`Seated ${result.seatedGuests.length} guest(s) at Table ${result.tableNumber}${result.skippedGuests.length ? ` (${result.skippedGuests.length} skipped)` : ''}`);
                        } catch (err: any) {
                          setError(err?.message || 'Failed to seat family');
                        }
                      }}
                      title={`Auto-seat ${group.name} family`}
                      aria-label={`Seat ${group.name} family together`}
                    >
                      🪑 Seat
                    </button>
                  )}
                  <div style={styles.toggleContainer}>
                    <span style={styles.toggleLabel}>{group.active ? 'Active' : 'Inactive'}</span>
                    <input
                      type="checkbox"
                      checked={group.active}
                      onChange={() => handleToggleActive(group)}
                      aria-label={`Toggle ${group.name} active status`}
                    />
                  </div>
                  <button
                    style={styles.iconBtn}
                    onClick={() => { setEditingId(group.id); setEditName(group.name); }}
                    title="Edit name"
                    aria-label={`Edit ${group.name}`}
                  >
                    ✏️
                  </button>
                  {confirmDeleteId === group.id ? (
                    <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                      <button style={styles.btnDanger} onClick={() => handleDelete(group.id)}>Confirm</button>
                      <button style={styles.btnSecondary} onClick={() => setConfirmDeleteId(null)}>Cancel</button>
                    </div>
                  ) : (
                    <button
                      style={styles.iconBtn}
                      onClick={() => setConfirmDeleteId(group.id)}
                      title="Delete"
                      aria-label={`Delete ${group.name}`}
                    >
                      🗑️
                    </button>
                  )}
                </div>
              </div>

              {expandedId === group.id && (
                <div style={styles.cardBody}>
                  {group.members.length === 0 && (
                    <div style={{ fontSize: '0.78rem', color: TEXT_MUTED, padding: '0.3rem 0' }}>
                      No members in this group yet.
                    </div>
                  )}
                  {group.members.length > 0 && (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                      <thead>
                        <tr style={{ borderBottom: `1px solid ${BORDER_LIGHT}` }}>
                          <th style={{ textAlign: 'left', padding: '0.3rem 0.4rem', color: TEXT_MUTED, fontWeight: 600 }}>Name</th>
                          <th style={{ textAlign: 'left', padding: '0.3rem 0.4rem', color: TEXT_MUTED, fontWeight: 600 }}>Order ID</th>
                          <th style={{ textAlign: 'left', padding: '0.3rem 0.4rem', color: TEXT_MUTED, fontWeight: 600 }}>Chapter</th>
                          <th style={{ textAlign: 'left', padding: '0.3rem 0.4rem', color: TEXT_MUTED, fontWeight: 600 }}>Ticket</th>
                          <th style={{ textAlign: 'left', padding: '0.3rem 0.4rem', color: TEXT_MUTED, fontWeight: 600 }}>Tier</th>
                          <th style={{ textAlign: 'left', padding: '0.3rem 0.4rem', color: TEXT_MUTED, fontWeight: 600 }}>Zone</th>
                          <th style={{ textAlign: 'left', padding: '0.3rem 0.4rem', color: TEXT_MUTED, fontWeight: 600 }}>Table</th>
                          <th style={{ width: '30px' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.members.map((member) => (
                          <tr key={member.id} style={{ borderBottom: `1px solid ${BORDER_LIGHT}` }}>
                            <td style={{ padding: '0.35rem 0.4rem', fontWeight: 500 }}>{member.fullName}</td>
                            <td style={{ padding: '0.35rem 0.4rem', color: '#854d0e' }}>{member.orderId || '—'}</td>
                            <td style={{ padding: '0.35rem 0.4rem', color: '#6b21a8' }}>{member.chapterAffiliation || '—'}</td>
                            <td style={{ padding: '0.35rem 0.4rem' }}>{member.ticketNumber || '—'}</td>
                            <td style={{ padding: '0.35rem 0.4rem', color: '#1e40af' }}>{member.tierName || '—'}</td>
                            <td style={{ padding: '0.35rem 0.4rem', color: '#0e7490' }}>{member.zoneName || '—'}</td>
                            <td style={{ padding: '0.35rem 0.4rem', color: '#166534' }}>{member.tableName || '—'}</td>
                            <td style={{ padding: '0.35rem 0.4rem' }}>
                              <button
                                style={styles.iconBtn}
                                onClick={() => handleRemoveMember(group.id, member.id)}
                                title="Remove from group"
                                aria-label={`Remove ${member.fullName} from ${group.name}`}
                              >
                                ✕
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}

                  {/* Add Members Button */}
                  <div style={{ ...styles.addMemberRow, justifyContent: 'flex-start' }}>
                    <button
                      style={styles.btnSecondary}
                      onClick={() => setAddMemberModalGroupId(group.id)}
                      aria-label={`Add members to ${group.name}`}
                    >
                      ➕ Add Members
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </>
      )}

      {/* ─── Task 9.1: Seated (Locked) Groups ────────────────────────────────── */}
      {viewMode === 'cards' && lockedGroups.length > 0 && (
        <>
          <div style={styles.sectionTitle}>Seated Groups</div>
          {lockedGroups.map((group) => (
            <div key={group.id} style={styles.cardLocked}>
              <div
                style={styles.cardHeader}
                onClick={() => setExpandedId(expandedId === group.id ? null : group.id)}
                role="button"
                aria-expanded={expandedId === group.id}
                aria-label={`Toggle ${group.name} details`}
              >
                <div style={styles.cardLeft}>
                  <span style={{ fontSize: '0.75rem', color: TEXT_MUTED }}>
                    {expandedId === group.id ? '▼' : '▶'}
                  </span>
                  <span style={{ fontSize: '0.85rem' }}>🔒</span>
                  <span style={styles.groupName}>{group.name}</span>
                  <span style={styles.badge}>{group.memberCount} member{group.memberCount !== 1 ? 's' : ''}</span>
                  <span style={styles.seatedBadge}>✅ Seated</span>
                </div>

                <div style={styles.cardActions} onClick={(e) => e.stopPropagation()}>
                  {/* Add Members button — works even on locked groups */}
                  <button
                    style={{ ...styles.iconBtn, fontSize: '0.72rem', color: PRIMARY, fontWeight: 600 }}
                    onClick={() => {
                      setAddMemberModalGroupId(group.id);
                      setPendingMembers([]);
                      setSearchQuery('');
                      setSearchResults([]);
                    }}
                    title={`Add members to ${group.name}`}
                    aria-label={`Add members to ${group.name}`}
                  >
                    ➕ Add Members
                  </button>
                  {/* Task 9.2: Unseat All button */}
                  <button
                    style={styles.unseatBtn}
                    onClick={() => handleUnseat(group)}
                    title={`Unseat all members of ${group.name}`}
                    aria-label={`Unseat all members of ${group.name}`}
                  >
                    Unseat All
                  </button>
                </div>
              </div>

              {/* Placement info */}
              {group.placement && (
                <div style={{ padding: '0 0.85rem 0.4rem', ...styles.placementInfo }}>
                  📍 {group.placement.zone} — Table{group.placement.tables.length !== 1 ? 's' : ''}{' '}
                  {group.placement.tables.map((t) => t.tableName || `#${t.tableNumber}`).join(', ')}
                </div>
              )}

              {expandedId === group.id && (
                <div style={styles.cardBody}>
                  {group.members.length > 0 && (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                      <thead>
                        <tr style={{ borderBottom: `1px solid ${BORDER_LIGHT}` }}>
                          <th style={{ textAlign: 'left', padding: '0.3rem 0.4rem', color: TEXT_MUTED, fontWeight: 600 }}>Name</th>
                          <th style={{ textAlign: 'left', padding: '0.3rem 0.4rem', color: TEXT_MUTED, fontWeight: 600 }}>Order ID</th>
                          <th style={{ textAlign: 'left', padding: '0.3rem 0.4rem', color: TEXT_MUTED, fontWeight: 600 }}>Chapter</th>
                          <th style={{ textAlign: 'left', padding: '0.3rem 0.4rem', color: TEXT_MUTED, fontWeight: 600 }}>Ticket</th>
                          <th style={{ textAlign: 'left', padding: '0.3rem 0.4rem', color: TEXT_MUTED, fontWeight: 600 }}>Zone</th>
                          <th style={{ textAlign: 'left', padding: '0.3rem 0.4rem', color: TEXT_MUTED, fontWeight: 600 }}>Table</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.members.map((member) => (
                          <tr key={member.id} style={{ borderBottom: `1px solid ${BORDER_LIGHT}` }}>
                            <td style={{ padding: '0.35rem 0.4rem', fontWeight: 500 }}>{member.fullName}</td>
                            <td style={{ padding: '0.35rem 0.4rem', color: '#854d0e' }}>{member.orderId || '—'}</td>
                            <td style={{ padding: '0.35rem 0.4rem', color: '#6b21a8' }}>{member.chapterAffiliation || '—'}</td>
                            <td style={{ padding: '0.35rem 0.4rem' }}>{member.ticketNumber || '—'}</td>
                            <td style={{ padding: '0.35rem 0.4rem', color: '#0e7490' }}>{member.zoneName || '—'}</td>
                            <td style={{ padding: '0.35rem 0.4rem', color: '#166534' }}>{member.tableName || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}

                  {/* Add Members Button — available even on locked/seated groups */}
                  <div style={{ ...styles.addMemberRow, justifyContent: 'flex-start' }}>
                    <button
                      style={styles.btnSecondary}
                      onClick={() => {
                        setAddMemberModalGroupId(group.id);
                        setPendingMembers([]);
                        setSearchQuery('');
                        setSearchResults([]);
                      }}
                      aria-label={`Add members to ${group.name}`}
                    >
                      ➕ Add Members
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </>
      )}

      {/* ─── Inactive Groups ─────────────────────────────────────────────────── */}
      {viewMode === 'cards' && inactiveGroups.length > 0 && (
        <>
          <div style={styles.sectionTitle}>Inactive Groups</div>
          {inactiveGroups.map((group) => (
            <div key={group.id} style={styles.cardInactive}>
              <div
                style={styles.cardHeader}
                onClick={() => setExpandedId(expandedId === group.id ? null : group.id)}
                role="button"
                aria-expanded={expandedId === group.id}
                aria-label={`Toggle ${group.name} details`}
              >
                <div style={styles.cardLeft}>
                  <span style={{ fontSize: '0.75rem', color: TEXT_MUTED }}>
                    {expandedId === group.id ? '▼' : '▶'}
                  </span>
                  {editingId === group.id ? (
                    <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
                      <input
                        style={styles.editInput}
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveEdit(group.id);
                          if (e.key === 'Escape') setEditingId(null);
                        }}
                        autoFocus
                        aria-label="Edit group name"
                      />
                      <button style={styles.iconBtn} onClick={() => handleSaveEdit(group.id)} title="Save" aria-label="Save name">✓</button>
                      <button style={styles.iconBtn} onClick={() => setEditingId(null)} title="Cancel" aria-label="Cancel edit">✕</button>
                    </div>
                  ) : (
                    <span style={styles.groupName}>{group.name}</span>
                  )}
                  <span style={styles.badge}>{group.memberCount} member{group.memberCount !== 1 ? 's' : ''}</span>
                  <span style={styles.inactiveBadge}>Inactive</span>
                </div>

                <div style={styles.cardActions} onClick={(e) => e.stopPropagation()}>
                  <div style={styles.toggleContainer}>
                    <span style={styles.toggleLabel}>Inactive</span>
                    <input
                      type="checkbox"
                      checked={group.active}
                      onChange={() => handleToggleActive(group)}
                      aria-label={`Toggle ${group.name} active status`}
                    />
                  </div>
                  <button
                    style={styles.iconBtn}
                    onClick={() => { setEditingId(group.id); setEditName(group.name); }}
                    title="Edit name"
                    aria-label={`Edit ${group.name}`}
                  >
                    ✏️
                  </button>
                  {confirmDeleteId === group.id ? (
                    <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                      <button style={styles.btnDanger} onClick={() => handleDelete(group.id)}>Confirm</button>
                      <button style={styles.btnSecondary} onClick={() => setConfirmDeleteId(null)}>Cancel</button>
                    </div>
                  ) : (
                    <button
                      style={styles.iconBtn}
                      onClick={() => setConfirmDeleteId(group.id)}
                      title="Delete"
                      aria-label={`Delete ${group.name}`}
                    >
                      🗑️
                    </button>
                  )}
                </div>
              </div>

              {expandedId === group.id && (
                <div style={styles.cardBody}>
                  {group.members.length === 0 && (
                    <div style={{ fontSize: '0.78rem', color: TEXT_MUTED, padding: '0.3rem 0' }}>
                      No members in this group.
                    </div>
                  )}
                  {group.members.length > 0 && (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                      <thead>
                        <tr style={{ borderBottom: `1px solid ${BORDER_LIGHT}` }}>
                          <th style={{ textAlign: 'left', padding: '0.3rem 0.4rem', color: TEXT_MUTED, fontWeight: 600 }}>Name</th>
                          <th style={{ textAlign: 'left', padding: '0.3rem 0.4rem', color: TEXT_MUTED, fontWeight: 600 }}>Order ID</th>
                          <th style={{ textAlign: 'left', padding: '0.3rem 0.4rem', color: TEXT_MUTED, fontWeight: 600 }}>Chapter</th>
                          <th style={{ textAlign: 'left', padding: '0.3rem 0.4rem', color: TEXT_MUTED, fontWeight: 600 }}>Ticket</th>
                          <th style={{ textAlign: 'left', padding: '0.3rem 0.4rem', color: TEXT_MUTED, fontWeight: 600 }}>Tier</th>
                          <th style={{ textAlign: 'left', padding: '0.3rem 0.4rem', color: TEXT_MUTED, fontWeight: 600 }}>Table</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.members.map((member) => (
                          <tr key={member.id} style={{ borderBottom: `1px solid ${BORDER_LIGHT}` }}>
                            <td style={{ padding: '0.35rem 0.4rem', fontWeight: 500 }}>{member.fullName}</td>
                            <td style={{ padding: '0.35rem 0.4rem', color: '#854d0e' }}>{member.orderId || '—'}</td>
                            <td style={{ padding: '0.35rem 0.4rem', color: '#6b21a8' }}>{member.chapterAffiliation || '—'}</td>
                            <td style={{ padding: '0.35rem 0.4rem' }}>{member.ticketNumber || '—'}</td>
                            <td style={{ padding: '0.35rem 0.4rem', color: '#1e40af' }}>{member.tierName || '—'}</td>
                            <td style={{ padding: '0.35rem 0.4rem', color: '#166534' }}>{member.tableName || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          ))}
        </>
      )}

      {/* ─── 3-PANE TABLE VIEW ─────────────────────────────────────────────────── */}
      {viewMode === 'table' && (() => {
        const selectedGroup = groups.find((g) => g.id === tableViewSelectedId) || null;
        const renderSection = (title: string, sectionGroups: FamilyGroup[], statusBadge?: React.ReactNode) => {
          if (sectionGroups.length === 0) return null;
          return (
            <div key={title} style={{ marginBottom: '1.5rem' }}>
              <div style={{ ...styles.sectionTitle, marginTop: '0.5rem' }}>{title} ({sectionGroups.length})</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: '0.75rem', border: `1px solid ${BORDER_LIGHT}`, borderRadius: '6px', overflow: 'hidden' }}>
                {/* Pane 1: Group List */}
                <div style={{ borderRight: `1px solid ${BORDER_LIGHT}`, maxHeight: '400px', overflowY: 'auto' }}>
                  <div style={{ padding: '0.5rem 0.6rem', backgroundColor: BG_SURFACE, borderBottom: `1px solid ${BORDER_LIGHT}`, fontSize: '0.72rem', fontWeight: 700, color: TEXT_MUTED, textTransform: 'uppercase' as const, position: 'sticky' as const, top: 0, zIndex: 1 }}>
                    Group Name / Members
                  </div>
                  {sectionGroups.map((g) => {
                    const allSeated = g.memberCount > 0 && g.members.length > 0 && g.members.every((m) => m.tableName);
                    const isSelected = tableViewSelectedId === g.id;
                    return (
                      <div
                        key={g.id}
                        onClick={() => setTableViewSelectedId(g.id)}
                        style={{
                          padding: '0.5rem 0.6rem',
                          cursor: 'pointer',
                          backgroundColor: isSelected ? '#c8e6c9' : allSeated ? '#e8f5e9' : BG_CARD,
                          borderBottom: `1px solid ${BORDER_LIGHT}`,
                          borderLeft: isSelected ? `3px solid ${PRIMARY}` : allSeated ? '3px solid #66bb6a' : '3px solid transparent',
                          transition: 'background-color 0.15s',
                        }}
                        role="button"
                        aria-label={`Select ${g.name}`}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                          {allSeated && <span style={{ fontSize: '0.72rem' }}>✅</span>}
                          <span style={{ fontSize: '0.82rem', fontWeight: 600, color: TEXT_DARK }}>{g.name}</span>
                        </div>
                        <div style={{ fontSize: '0.72rem', color: allSeated ? '#2e7d32' : TEXT_MUTED, marginTop: '0.15rem' }}>
                          {g.memberCount} member{g.memberCount !== 1 ? 's' : ''}
                          {allSeated && <span style={{ marginLeft: '0.4rem', fontWeight: 600 }}>• All seated</span>}
                          {statusBadge}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Pane 2: Table Names */}
                <div style={{ borderRight: `1px solid ${BORDER_LIGHT}`, maxHeight: '360px', overflowY: 'auto' }}>
                  <div style={{ padding: '0.5rem 0.6rem', backgroundColor: BG_SURFACE, borderBottom: `1px solid ${BORDER_LIGHT}`, fontSize: '0.72rem', fontWeight: 700, color: TEXT_MUTED, textTransform: 'uppercase' as const }}>
                    Table Assignment
                  </div>
                  {selectedGroup && sectionGroups.some((g) => g.id === selectedGroup.id) ? (
                    <div style={{ padding: '0.6rem' }}>
                      {selectedGroup.placement ? (
                        <>
                          <div style={{ fontSize: '0.78rem', color: TEXT_MUTED, marginBottom: '0.4rem' }}>
                            Zone: <strong style={{ color: '#0e7490' }}>{selectedGroup.placement.zone}</strong>
                          </div>
                          {selectedGroup.placement.tables.map((t, idx) => (
                            <div key={idx} style={{ padding: '0.35rem 0.5rem', marginBottom: '0.3rem', backgroundColor: '#e8f5e9', borderRadius: '4px', fontSize: '0.8rem', color: '#166534', fontWeight: 500 }}>
                              {t.tableName || `Table #${t.tableNumber}`}
                              <span style={{ fontSize: '0.7rem', color: TEXT_MUTED, marginLeft: '0.4rem' }}>({t.zoneName})</span>
                            </div>
                          ))}
                        </>
                      ) : (
                        <div style={{ fontSize: '0.78rem', color: TEXT_MUTED, padding: '0.5rem 0' }}>
                          No table assigned yet.
                        </div>
                      )}
                      {/* Show unique tables from members if no placement data */}
                      {!selectedGroup.placement && selectedGroup.members.some((m) => m.tableName) && (
                        <>
                          <div style={{ fontSize: '0.72rem', color: TEXT_MUTED, marginBottom: '0.3rem' }}>Tables from members:</div>
                          {[...new Set(selectedGroup.members.filter((m) => m.tableName).map((m) => m.tableName))].map((tName, idx) => (
                            <div key={idx} style={{ padding: '0.35rem 0.5rem', marginBottom: '0.3rem', backgroundColor: BG_SURFACE, borderRadius: '4px', fontSize: '0.8rem', color: TEXT_DARK }}>
                              {tName}
                            </div>
                          ))}
                        </>
                      )}

                      {/* Group Insights */}
                      {selectedGroup.members.length > 0 && (() => {
                        const uniqueOrders = new Set(selectedGroup.members.filter((m) => m.orderId).map((m) => m.orderId)).size;
                        const uniqueChapters = new Set(selectedGroup.members.filter((m) => m.chapterAffiliation).map((m) => m.chapterAffiliation)).size;
                        const uniqueZones = new Set(selectedGroup.members.filter((m) => m.zoneName).map((m) => m.zoneName)).size;
                        const uniqueTables = new Set(selectedGroup.members.filter((m) => m.tableName).map((m) => m.tableName)).size;
                        const seatedCount = selectedGroup.members.filter((m) => m.tableName).length;
                        return (
                          <div style={{ marginTop: '0.75rem', paddingTop: '0.6rem', borderTop: `1px dashed ${BORDER_LIGHT}` }}>
                            <div style={{ fontSize: '0.7rem', fontWeight: 700, color: TEXT_MUTED, textTransform: 'uppercase' as const, marginBottom: '0.35rem' }}>
                              Insights
                            </div>
                            <ul style={{ margin: 0, paddingLeft: '1rem', fontSize: '0.75rem', color: TEXT_DARK, lineHeight: 1.7 }}>
                              <li><span style={{ color: '#854d0e' }}>{uniqueOrders}</span> unique order{uniqueOrders !== 1 ? 's' : ''}</li>
                              <li><span style={{ color: '#6b21a8' }}>{uniqueChapters}</span> chapter{uniqueChapters !== 1 ? 's' : ''} represented</li>
                              {uniqueZones > 0 && <li><span style={{ color: '#0e7490' }}>{uniqueZones}</span> zone{uniqueZones !== 1 ? 's' : ''}</li>}
                              {uniqueTables > 0 && <li><span style={{ color: '#166534' }}>{uniqueTables}</span> table{uniqueTables !== 1 ? 's' : ''}</li>}
                              <li>{seatedCount}/{selectedGroup.members.length} seated</li>
                            </ul>
                          </div>
                        );
                      })()}
                    </div>
                  ) : (
                    <div style={{ padding: '1rem', fontSize: '0.78rem', color: TEXT_MUTED, textAlign: 'center' as const }}>
                      ← Select a group
                    </div>
                  )}
                </div>

                {/* Pane 3: Member Details */}
                <div style={{ maxHeight: '360px', overflowY: 'auto' }}>
                  <div style={{ padding: '0.5rem 0.6rem', backgroundColor: BG_SURFACE, borderBottom: `1px solid ${BORDER_LIGHT}`, fontSize: '0.72rem', fontWeight: 700, color: TEXT_MUTED, textTransform: 'uppercase' as const }}>
                    Members
                  </div>
                  {selectedGroup && sectionGroups.some((g) => g.id === selectedGroup.id) ? (
                    selectedGroup.members.length > 0 ? (
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.76rem' }}>
                        <thead>
                          <tr style={{ backgroundColor: BG_SURFACE }}>
                            <th style={{ textAlign: 'left', padding: '0.35rem 0.4rem', color: TEXT_MUTED, fontWeight: 600, borderBottom: `1px solid ${BORDER_LIGHT}` }}>Name</th>
                            <th style={{ textAlign: 'left', padding: '0.35rem 0.4rem', color: TEXT_MUTED, fontWeight: 600, borderBottom: `1px solid ${BORDER_LIGHT}` }}>Order ID</th>
                            <th style={{ textAlign: 'left', padding: '0.35rem 0.4rem', color: TEXT_MUTED, fontWeight: 600, borderBottom: `1px solid ${BORDER_LIGHT}` }}>Chapter</th>
                            <th style={{ textAlign: 'left', padding: '0.35rem 0.4rem', color: TEXT_MUTED, fontWeight: 600, borderBottom: `1px solid ${BORDER_LIGHT}` }}>Ticket #</th>
                            <th style={{ textAlign: 'left', padding: '0.35rem 0.4rem', color: TEXT_MUTED, fontWeight: 600, borderBottom: `1px solid ${BORDER_LIGHT}` }}>Zone</th>
                            <th style={{ textAlign: 'left', padding: '0.35rem 0.4rem', color: TEXT_MUTED, fontWeight: 600, borderBottom: `1px solid ${BORDER_LIGHT}` }}>Table</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedGroup.members.map((m) => (
                            <tr key={m.id} style={{ borderBottom: `1px solid ${BORDER_LIGHT}` }}>
                              <td style={{ padding: '0.35rem 0.4rem', fontWeight: 500, color: TEXT_DARK }}>{m.fullName}</td>
                              <td style={{ padding: '0.35rem 0.4rem', color: '#854d0e' }}>{m.orderId || '—'}</td>
                              <td style={{ padding: '0.35rem 0.4rem', color: '#6b21a8' }}>{m.chapterAffiliation || '—'}</td>
                              <td style={{ padding: '0.35rem 0.4rem' }}>{m.ticketNumber || '—'}</td>
                              <td style={{ padding: '0.35rem 0.4rem', color: '#0e7490' }}>{m.zoneName || '—'}</td>
                              <td style={{ padding: '0.35rem 0.4rem', color: '#166534' }}>{m.tableName || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div style={{ padding: '1rem', fontSize: '0.78rem', color: TEXT_MUTED, textAlign: 'center' as const }}>
                        No members in this group.
                      </div>
                    )
                  ) : (
                    <div style={{ padding: '1rem', fontSize: '0.78rem', color: TEXT_MUTED, textAlign: 'center' as const }}>
                      ← Select a group to view members
                    </div>
                  )}
                  {/* Add Members button — available for all groups including locked */}
                  {selectedGroup && sectionGroups.some((g) => g.id === selectedGroup.id) && (
                    <div style={{ padding: '0.5rem 0.6rem', borderTop: `1px solid ${BORDER_LIGHT}` }}>
                      <button
                        style={styles.btnSecondary}
                        onClick={() => {
                          setAddMemberModalGroupId(selectedGroup.id);
                          setPendingMembers([]);
                          setSearchQuery('');
                          setSearchResults([]);
                        }}
                        aria-label={`Add members to ${selectedGroup.name}`}
                      >
                        ➕ Add Members
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        };

        return (
          <>
            {renderSection('Active', selectableGroups)}
            {renderSection('Seated', lockedGroups)}
            {renderSection('Inactive', inactiveGroups)}
            {selectableGroups.length === 0 && lockedGroups.length === 0 && inactiveGroups.length === 0 && (
              <div style={styles.emptyState}>No family groups to display.</div>
            )}
          </>
        );
      })()}

      {/* ─── Task 8.1: Seating Configuration Panel (Modal) ────────────────────── */}
      {showConfigPanel && (
        <div style={styles.configOverlay} onClick={() => !seatingInProgress && setShowConfigPanel(false)}>
          <div
            style={styles.configPanel}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Seating configuration"
          >
            <div style={styles.configTitle}>Configure Batch Seating</div>

            <div style={styles.configField}>
              <label style={styles.configLabel} htmlFor="zone-select">Zone Preference</label>
              <select
                id="zone-select"
                style={styles.configSelect}
                value={configZoneId || ''}
                onChange={(e) => setConfigZoneId(e.target.value || null)}
                disabled={seatingInProgress}
              >
                <option value="">Any available zone</option>
                {zones.map((zone) => (
                  <option key={zone.id} value={zone.id}>{zone.name}</option>
                ))}
              </select>
            </div>

            <div style={styles.configField}>
              <span style={styles.configLabel}>Assignment Rule</span>
              <div style={styles.configRadioGroup}>
                <label style={styles.configRadioLabel}>
                  <input
                    type="radio"
                    name="assignment-rule"
                    value="keep_together"
                    checked={configRule === 'keep_together'}
                    onChange={() => setConfigRule('keep_together')}
                    disabled={seatingInProgress}
                  />
                  Keep at same table
                </label>
                <label style={styles.configRadioLabel}>
                  <input
                    type="radio"
                    name="assignment-rule"
                    value="allow_split"
                    checked={configRule === 'allow_split'}
                    onChange={() => setConfigRule('allow_split')}
                    disabled={seatingInProgress}
                  />
                  Allow splitting across adjacent tables
                </label>
                <label style={styles.configRadioLabel}>
                  <input
                    type="radio"
                    name="assignment-rule"
                    value="start_new_table"
                    checked={configRule === 'start_new_table'}
                    onChange={() => setConfigRule('start_new_table')}
                    disabled={seatingInProgress}
                  />
                  Start new table (requires empty table with 10 seats in zone)
                </label>
              </div>
            </div>

            <div style={{ fontSize: '0.78rem', color: TEXT_MUTED, marginTop: '0.5rem' }}>
              Seating {selectedGroupIds.size} group{selectedGroupIds.size !== 1 ? 's' : ''} ({selectedMemberCount} member{selectedMemberCount !== 1 ? 's' : ''})
            </div>

            {error && (
              <div style={{ padding: '8px 12px', backgroundColor: '#fdecea', border: '1px solid #f5c6cb', borderRadius: '6px', marginTop: '0.75rem', fontSize: '0.82rem', color: '#d32f2f' }}>
                {error}
              </div>
            )}

            {configRule === 'start_new_table' && !configZoneId && (
              <div style={{ padding: '6px 10px', backgroundColor: '#fffbea', border: '1px solid #f59e0b', borderRadius: '6px', marginTop: '0.5rem', fontSize: '0.78rem', color: '#92400e' }}>
                ⚠️ A zone must be selected for "Start New Table" rule.
              </div>
            )}

            <div style={styles.configActions}>
              <button
                style={styles.btnSecondary}
                onClick={() => setShowConfigPanel(false)}
                disabled={seatingInProgress}
              >
                Cancel
              </button>
              <button
                style={styles.btnPrimary}
                onClick={handleConfirmSeat}
                disabled={seatingInProgress || (configRule === 'start_new_table' && !configZoneId)}
              >
                {seatingInProgress ? 'Seating...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Add Members Modal ────────────────────────────────────────────────── */}
      {addMemberModalGroupId && (() => {
        const modalGroup = groups.find((g) => g.id === addMemberModalGroupId);
        if (!modalGroup) return null;
        return (
          <div style={styles.configOverlay} onClick={() => !savingMembers && handleCloseAddMemberModal()}>
            <div
              style={{ ...styles.configPanel, width: '480px' }}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label={`Add members to ${modalGroup.name}`}
            >
              <div style={styles.configTitle}>Add Members to {modalGroup.name}</div>

              {/* Search Input */}
              <div style={styles.configField}>
                <input
                  style={{ ...styles.input, width: '100%' }}
                  type="text"
                  placeholder="Search guests by name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                  disabled={savingMembers}
                  aria-label="Search guests to add"
                />
              </div>

              {/* Search Results */}
              <div style={{ maxHeight: '200px', overflowY: 'auto', border: `1px solid ${BORDER_LIGHT}`, borderRadius: '4px', marginBottom: '0.75rem' }}>
                {searching && (
                  <div style={{ padding: '0.6rem', fontSize: '0.78rem', color: TEXT_MUTED, textAlign: 'center' as const }}>
                    Searching...
                  </div>
                )}
                {!searching && searchQuery.length >= 2 && searchResults.length === 0 && (
                  <div style={{ padding: '0.6rem', fontSize: '0.78rem', color: TEXT_MUTED, textAlign: 'center' as const }}>
                    No guests found.
                  </div>
                )}
                {!searching && searchQuery.length < 2 && pendingMembers.length === 0 && (
                  <div style={{ padding: '0.6rem', fontSize: '0.78rem', color: TEXT_MUTED, textAlign: 'center' as const }}>
                    Type at least 2 characters to search.
                  </div>
                )}
                {searchResults.map((guest) => (
                  <div
                    key={guest.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.45rem 0.6rem',
                      fontSize: '0.8rem',
                      borderBottom: `1px solid ${BORDER_LIGHT}`,
                      cursor: 'pointer',
                      color: TEXT_DARK,
                    }}
                    onClick={() => handleTogglePendingMember(guest)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && handleTogglePendingMember(guest)}
                  >
                    <input
                      type="checkbox"
                      checked={pendingMembers.some((m) => m.id === guest.id)}
                      onChange={() => handleTogglePendingMember(guest)}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Select ${guest.fullName}`}
                    />
                    <span>{guest.fullName}</span>
                    {guest.ticketNumber && (
                      <span style={{ color: TEXT_MUTED, fontSize: '0.72rem' }}>
                        ({guest.ticketNumber})
                      </span>
                    )}
                    {guest.familyAssociation && (
                      <span style={{ fontSize: '0.68rem', color: '#7c3aed', backgroundColor: '#ede9fe', padding: '1px 6px', borderRadius: '8px', marginLeft: 'auto' }}>
                        👨‍👩‍👧 {guest.familyAssociation}
                      </span>
                    )}
                  </div>
                ))}
              </div>

              {/* Selected Members Section */}
              {pendingMembers.length > 0 && (
                <div style={{ marginBottom: '0.75rem' }}>
                  <div style={{ fontSize: '0.78rem', fontWeight: 600, color: TEXT_DARK, marginBottom: '0.35rem' }}>
                    Selected ({pendingMembers.length})
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: '0.35rem' }}>
                    {pendingMembers.map((member) => (
                      <span
                        key={member.id}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          padding: '0.2rem 0.5rem',
                          fontSize: '0.75rem',
                          backgroundColor: BG_SURFACE,
                          border: `1px solid ${BORDER_LIGHT}`,
                          borderRadius: '12px',
                          color: TEXT_DARK,
                        }}
                      >
                        {member.fullName}
                        <button
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', fontSize: '0.8rem', lineHeight: 1, color: TEXT_MUTED }}
                          onClick={() => handleRemovePendingMember(member.id)}
                          aria-label={`Remove ${member.fullName} from selection`}
                        >
                          ✕
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div style={styles.configActions}>
                <button
                  style={styles.btnSecondary}
                  onClick={handleCloseAddMemberModal}
                  disabled={savingMembers}
                >
                  Cancel
                </button>
                <button
                  style={styles.btnPrimary}
                  onClick={handleSaveMembers}
                  disabled={savingMembers || pendingMembers.length === 0}
                >
                  {savingMembers ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
